<template>
  <div class="page-content">
    <breadcrumb-index 
      :items="breadcrumbs"
      icon="file-text"
      :badge="totalCount ? `${totalCount} notes` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button v-if="canModify" @click="openCreateModal" class="btn btn-primary btn-sm" :disabled="!selectedSubject">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Note
        </button>
      </template>
    </breadcrumb-index>

    <!-- Cascading Filter Section -->
    <div class="filter-cascade-section">
      <div class="filter-cascade-header">
        <vue-feather type="filter" size="18" />
        <h4>Select Category to View Notes</h4>
      </div>
      <div class="filter-cascade-grid">
        <!-- Step 1: Select Exam -->
        <div class="filter-step" :class="{ active: true, completed: selectedExam }">
          <div class="step-number">1</div>
          <div class="step-content">
            <label class="step-label">Select Exam</label>
            <select v-model="selectedExam" class="filter-select" @change="onExamChange" :disabled="loadingExams">
              <option value="">{{ loadingExams ? 'Loading exams...' : 'Choose an exam...' }}</option>
              <option v-for="exam in exams" :key="exam.id" :value="exam.id">
                {{ exam.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Step 2: Select Subject -->
        <div class="filter-step" :class="{ active: selectedExam, completed: selectedSubject, disabled: !selectedExam }">
          <div class="step-number">2</div>
          <div class="step-content">
            <label class="step-label">Select Subject</label>
            <select v-model="selectedSubject" class="filter-select" :disabled="!selectedExam || loadingSubjects" @change="onSubjectChange">
              <option value="">{{ loadingSubjects ? 'Loading subjects...' : 'Choose a subject...' }}</option>
              <option v-for="subject in filteredSubjects" :key="subject.id" :value="subject.id">
                {{ subject.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Optional Step 3: Filter by Topic -->
        <div class="filter-step optional" :class="{ active: selectedSubject, completed: selectedTopic, disabled: !selectedSubject }">
          <div class="step-number">3</div>
          <div class="step-content">
            <label class="step-label">Filter by Topic <span class="optional-tag">(Optional)</span></label>
            <select v-model="selectedTopic" class="filter-select" :disabled="!selectedSubject || loadingTopics" @change="fetchNotes">
              <option value="">{{ loadingTopics ? 'Loading topics...' : 'All Topics' }}</option>
              <option v-for="topic in filteredTopics" :key="topic.id" :value="topic.id">
                {{ topic.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Notes Table - Only show when subject is selected -->
    <div v-if="selectedSubject" class="notes-content">
      <DataTable
        :items="notes"
        :columns="columns"
        :loading="loading"
        :paginated="false"
        search-placeholder="Search notes..."
        empty-icon="file-text"
        empty-title="No notes found"
        :empty-description="emptyDescription"
      >
        <template #filters>
          <select v-model="filterType" class="filter-select-modern" @change="fetchNotes">
            <option value="">All Types</option>
            <option value="study_notes">Study Notes</option>
            <option value="topic_content">Topic Content</option>
            <option value="lesson_content">Lesson Content</option>
          </select>
          <select v-model="filterStatus" class="filter-select-modern" @change="fetchNotes">
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <select v-model="filterLanguage" class="filter-select-modern" @change="fetchNotes">
            <option value="">All Languages</option>
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
        </template>

        <template #cell-title="{ item }">
          <div class="avatar-info">
            <span class="avatar-name">{{ item.title }}</span>
            <span v-if="item.topicName" class="avatar-subtitle">{{ item.topicName }}</span>
          </div>
        </template>

        <template #cell-type="{ item }">
          <span :class="['badge', getTypeBadge(item.note_type || item.type)]">
            {{ formatType(item.note_type || item.type) }}
          </span>
        </template>

        <template #cell-status="{ item }">
          <span :class="['status-badge', item.status === 'published' ? 'status-active' : 'status-inactive']">
            {{ item.status }}
          </span>
        </template>

        <template #cell-created="{ item }">
          <span class="text-muted">{{ formatDate(item.createdAt) }}</span>
        </template>

        <template #cell-actions="{ item }">
          <div class="action-buttons">
            <button class="action-btn action-view" @click="openViewModal(item)" v-tooltip="'View details'">
              <vue-feather type="eye" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-edit" @click="openEditModal(item)" v-tooltip="'Edit note'">
              <vue-feather type="edit-2" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-delete" @click="confirmDelete(item)" v-tooltip="{ text: 'Delete note', variant: 'danger' }">
              <vue-feather type="trash-2" size="14" />
            </button>
          </div>
        </template>

        <template #empty-action>
          <button @click="openCreateModal" class="btn btn-primary">
            <vue-feather type="plus" size="16" class="me-1" />
            Add First Note
          </button>
        </template>
      </DataTable>

      <!-- Server-Side Pagination -->
      <div v-if="totalCount > 0" class="server-pagination">
        <div class="pagination-info">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalCount }}
        </div>
        <div class="pagination-controls">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(1)" title="First page">
            <vue-feather type="chevrons-left" size="16" />
          </button>
          <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)" title="Previous page">
            <vue-feather type="chevron-left" size="16" />
          </button>
          <div class="pagination-pages">
            <button v-for="page in visiblePages" :key="page" class="pagination-page" :class="{ active: page === currentPage, ellipsis: page === '...' }" :disabled="page === '...'" @click="page !== '...' && goToPage(page)">
              {{ page }}
            </button>
          </div>
          <button class="pagination-btn" :disabled="!hasMore" @click="goToPage(currentPage + 1)" title="Next page">
            <vue-feather type="chevron-right" size="16" />
          </button>
          <button class="pagination-btn" :disabled="!hasMore" @click="goToPage(totalPages)" title="Last page">
            <vue-feather type="chevrons-right" size="16" />
          </button>
        </div>
        <div class="pagination-size">
          <select v-model="pageSize" class="page-size-select" @change="onPageSizeChange">
            <option v-for="size in [10, 25, 50, 100]" :key="size" :value="size">{{ size }} per page</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Empty State when no subject selected -->
    <div v-else class="select-prompt">
      <div class="select-prompt-icon">
        <vue-feather type="layers" size="64" />
      </div>
      <h3>Select an Exam and Subject</h3>
      <p>Choose an exam and subject from the filters above to view and manage notes.</p>
    </div>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Note' : 'Add Note'"
      icon="file-text"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.title"
      :item-id="isEditing ? editingId : ''"
      @submit="saveNote"
    >
      <form @submit.prevent="saveNote" class="modal-form">
        <div class="form-group">
          <label class="form-label">Title <span class="text-danger">*</span></label>
          <input v-model="form.title" type="text" class="form-control" placeholder="e.g., Key Concepts Summary" required />
        </div>

        <!-- Assignment Section -->
        <div class="assignment-section">
          <label class="section-label">
            <vue-feather type="link" size="14" />
            Assignment
          </label>
          <div class="assignment-info">
            <span class="assignment-tag"><vue-feather type="book-open" size="12" /> {{ selectedExamName }}</span>
            <span class="assignment-tag"><vue-feather type="layers" size="12" /> {{ selectedSubjectName }}</span>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Topic</label>
              <select v-model="form.topic_id" class="form-control">
                <option value="">Select Topic</option>
                <option v-for="topic in filteredTopics" :key="topic.id" :value="topic.id">{{ topic.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Lesson</label>
              <select v-model="form.lesson_id" class="form-control" :disabled="!formLessons.length && !loadingLessons">
                <option value="">{{ loadingLessons ? 'Loading lessons...' : 'Select Lesson' }}</option>
                <option v-for="lesson in formLessons" :key="lesson.id" :value="lesson.id">{{ lesson.title }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- AI Enhance Button -->
        <div class="ai-enhance-section" v-if="form.title">
          <button type="button" @click="aiEnhanceNote" class="ai-enhance-btn" :disabled="aiEnhancing">
            <vue-feather :type="aiEnhancing ? 'loader' : 'cpu'" size="16" :class="{ 'spin': aiEnhancing }" />
            {{ aiEnhancing ? 'Generating...' : 'AI Generate Content' }}
          </button>
          <button type="button" @click="aiTranslateNote" class="ai-translate-btn" :disabled="aiTranslating">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'AI Translate' }}
          </button>
          <span class="ai-hint">Auto-generate comprehensive study notes</span>
        </div>

        <!-- Translation Language Selector -->
        <div class="ai-translate-section" v-if="form.title && showTranslateOptions">
          <label class="form-label">Translate to</label>
          <select v-model="translateTargetLang" class="form-control form-control-sm" style="max-width: 200px;">
            <option v-for="lang in languageOptions.filter(l => l.code !== form.language)" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <button type="button" @click="confirmTranslateNote" class="ai-translate-btn" :disabled="aiTranslating || !translateTargetLang">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'Translate Now' }}
          </button>
        </div>

        <div class="form-group">
          <label class="form-label">Content</label>
          <textarea v-model="form.content" class="form-control" rows="8" placeholder="Note content... (supports Markdown)"></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Note Type</label>
            <select v-model="form.note_type" class="form-control">
              <option value="study_notes">Study Notes</option>
              <option value="topic_content">Topic Content</option>
              <option value="lesson_content">Lesson Content</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Status</label>
            <select v-model="form.status" class="form-control">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Language</label>
          <select v-model="form.language" class="form-control" @change="onLanguageChange">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <small v-if="loadingTranslation" class="text-info"><vue-feather type="loader" size="12" class="spin" /> Loading translation...</small>
        </div>
      </form>
    </CreateModal>

    <!-- View Modal -->
    <NoteViewModal
      v-model="showViewModal"
      :note="viewingNote"
      :on-edit="() => { showViewModal = false; openEditModal(viewingNote); }"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'
import NoteViewModal from '@/components/modals/NoteViewModal.vue'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'NotesList',
  components: { DataTable, CreateModal, NoteViewModal },
  setup() {
    const authStore = useAuthStore()
    const alert = useAlert()
    const canModify = computed(() => authStore.isSuperAdmin || authStore.isAdmin || authStore.isContentManager)
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Notes', active: true }
    ]

    const columns = [
      { key: 'title', label: 'Title', sortable: true, minWidth: '250px' },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'created', label: 'Created', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    // Data
    const notes = ref([])
    const loading = ref(false)
    const totalCount = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const hasMore = ref(false)

    // Cascading filter state
    const exams = ref([])
    const subjects = ref([])
    const topics = ref([])
    const lessons = ref([])
    const selectedExam = ref('')
    const selectedSubject = ref('')
    const selectedTopic = ref('')
    const filterType = ref('')
    const filterStatus = ref('')
    const filterLanguage = ref('')

    // Modal state
    const showModal = ref(false)
    const showViewModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const viewingNote = ref(null)
    const aiEnhancing = ref(false)
    const aiTranslating = ref(false)
    const showTranslateOptions = ref(false)
    const translateTargetLang = ref('hi')
    const loadingTranslation = ref(false)

    const form = reactive({
      topic_id: '',
      lesson_id: '',
      title: '',
      content: '',
      note_type: 'study_notes',
      status: 'draft',
      language: 'en'
    })

    // Computed — data is already scoped from API, no client-side filtering needed
    const filteredSubjects = computed(() => subjects.value)

    const filteredTopics = computed(() => topics.value)

    const formLessons = computed(() => lessons.value)

    const selectedExamName = computed(() => {
      const exam = exams.value.find(e => e.id === selectedExam.value)
      return exam?.name || 'Not selected'
    })

    const selectedSubjectName = computed(() => {
      const subject = subjects.value.find(s => s.id === selectedSubject.value)
      return subject?.name || 'Not selected'
    })

    const emptyDescription = computed(() => {
      if (selectedTopic.value) return 'No notes found for this topic. Add your first note.'
      return 'No notes found for this subject. Add your first note or select a specific topic.'
    })

    // Pagination computed
    const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))
    const startIndex = computed(() => (currentPage.value - 1) * pageSize.value)
    const endIndex = computed(() => Math.min(startIndex.value + pageSize.value, totalCount.value))
    
    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)
        for (let i = start; i <= end; i++) pages.push(i)
        if (current < total - 2) pages.push('...')
        pages.push(total)
      }
      return pages
    })

    // Loading states for cascading dropdowns
    const loadingExams = ref(false)
    const loadingSubjects = ref(false)
    const loadingTopics = ref(false)
    const loadingLessons = ref(false)

    // Fetch only exams on mount — subjects/topics/lessons load on demand
    const fetchInitialData = async () => {
      loadingExams.value = true
      try {
        const examsRes = await api.get('/v1/admin/exams')
        exams.value = examsRes.data.exams || []
      } catch (err) {
        console.error('Failed to fetch exams:', err)
      } finally {
        loadingExams.value = false
      }
    }

    // Load subjects for the selected exam
    const fetchSubjectsForExam = async (examId) => {
      if (!examId) { subjects.value = []; return }
      loadingSubjects.value = true
      try {
        const res = await api.get(`/v1/admin/exams/${examId}/subjects`)
        subjects.value = res.data.subjects || []
      } catch (err) {
        console.error('Failed to fetch subjects:', err)
        subjects.value = []
      } finally {
        loadingSubjects.value = false
      }
    }

    // Load topics for the selected subject
    const fetchTopicsForSubject = async (subjectId) => {
      if (!subjectId) { topics.value = []; return }
      loadingTopics.value = true
      try {
        const res = await api.get(`/v1/admin/subjects/${subjectId}/topics`)
        topics.value = res.data.topics || []
      } catch (err) {
        console.error('Failed to fetch topics:', err)
        topics.value = []
      } finally {
        loadingTopics.value = false
      }
    }

    // Load lessons for a topic (used in form modal)
    const fetchLessonsForTopic = async (topicId) => {
      if (!topicId) { lessons.value = []; return }
      loadingLessons.value = true
      try {
        const res = await api.get(`/v1/admin/topics/${topicId}/lessons`)
        lessons.value = res.data.lessons || []
      } catch (err) {
        console.error('Failed to fetch lessons:', err)
        lessons.value = []
      } finally {
        loadingLessons.value = false
      }
    }

    // Fetch notes with filters and pagination
    const fetchNotes = async () => {
      if (!selectedSubject.value) {
        notes.value = []
        totalCount.value = 0
        hasMore.value = false
        return
      }

      loading.value = true
      try {
        const params = new URLSearchParams()
        params.append('subjectId', selectedSubject.value)
        if (selectedTopic.value) params.append('topicId', selectedTopic.value)
        if (filterType.value) params.append('note_type', filterType.value)
        if (filterStatus.value) params.append('status', filterStatus.value)
        if (filterLanguage.value) params.append('language', filterLanguage.value)
        params.append('offset', (currentPage.value - 1) * pageSize.value)
        params.append('limit', pageSize.value)

        const response = await api.get(`/v1/admin/notes?${params.toString()}`)
        notes.value = response.data.notes || []
        totalCount.value = response.data.total || 0
        hasMore.value = response.data.hasMore || false
      } catch (err) {
        console.error('Failed to fetch notes:', err)
        notes.value = []
        totalCount.value = 0
        hasMore.value = false
      } finally {
        loading.value = false
      }
    }

    // Event handlers — cascade API calls on selection change
    const onExamChange = async () => {
      selectedSubject.value = ''
      selectedTopic.value = ''
      subjects.value = []
      topics.value = []
      lessons.value = []
      notes.value = []
      totalCount.value = 0
      await fetchSubjectsForExam(selectedExam.value)
    }

    const onSubjectChange = async () => {
      selectedTopic.value = ''
      topics.value = []
      lessons.value = []
      currentPage.value = 1
      await fetchTopicsForSubject(selectedSubject.value)
      fetchNotes()
    }

    const goToPage = (page) => {
      currentPage.value = page
      fetchNotes()
    }

    const onPageSizeChange = () => {
      currentPage.value = 1
      fetchNotes()
    }

    // Form topic watcher — fetch lessons on demand when topic changes in form
    watch(() => form.topic_id, (newTopicId) => {
      form.lesson_id = ''
      fetchLessonsForTopic(newTopicId)
    })

    // Modal functions
    const resetForm = () => {
      form.topic_id = ''
      form.lesson_id = ''
      form.title = ''
      form.content = ''
      form.note_type = 'study_notes'
      form.status = 'draft'
      form.language = 'en'
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      if (!selectedSubject.value) return
      resetForm()
      showModal.value = true
    }

    const openViewModal = (note) => {
      viewingNote.value = note
      showViewModal.value = true
    }

    const openEditModal = (note) => {
      isEditing.value = true
      editingId.value = note.id
      form.title = note.title || ''
      form.content = note.content || ''
      form.note_type = note.note_type || note.type || 'study_notes'
      form.status = note.status || 'draft'
      form.language = note.language || 'en'
      form.topic_id = note.topic_id || note.topicId || ''
      form.lesson_id = note.lesson_id || note.lessonId || ''
      showModal.value = true
    }

    const saveNote = async () => {
      if (!form.title) return
      saving.value = true
      try {
        const payload = {
          exam_id: selectedExam.value,
          subject_id: selectedSubject.value,
          topic_id: form.topic_id || null,
          lesson_id: form.lesson_id || null,
          title: form.title,
          content: form.content,
          note_type: form.note_type,
          status: form.status,
          language_code: form.language
        }

        if (isEditing.value) {
          await api.put(`/v1/admin/notes/${editingId.value}`, payload)
          alert.success('Updated!', 'Note updated successfully')
        } else {
          await api.post('/v1/admin/notes', payload)
          alert.success('Created!', 'Note created successfully')
        }
        showModal.value = false
        resetForm()
        fetchNotes()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save note')
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (note) => {
      const result = await alert.delete('Delete Note?', `Are you sure you want to delete "${note.title}"?`)
      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/notes/${note.id}`)
          alert.success('Deleted!', 'Note has been deleted.')
          fetchNotes()
        } catch (err) {
          alert.error('Error', 'Failed to delete note')
        }
      }
    }

    // AI Enhancement
    const parseAINoteResponse = (response) => {
      // 1. If structured_data already has parsed content, use it directly
      const structuredContent = response.data?.structured_data?.data?.content
      if (structuredContent && typeof structuredContent === 'string') return structuredContent

      // If structured_data.data is an object with content, extract it
      const structuredData = response.data?.structured_data?.data
      if (structuredData && typeof structuredData === 'object' && structuredData.content) {
        return String(structuredData.content)
      }

      // 2. Get the raw response text
      let raw = response.data?.response || response.data?.message || response.data?.content || ''
      if (!raw && typeof response.data === 'string') raw = response.data
      if (typeof raw !== 'string') {
        if (raw && typeof raw === 'object') return raw.content || raw.notes || raw.text || JSON.stringify(raw)
        return ''
      }

      const trimmed = raw.trim()

      // 3. If it doesn't look like JSON, return as-is (it's already markdown)
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[') && !trimmed.includes('```json')) {
        return trimmed
      }

      // 4. Try to extract content from JSON string
      // Helper to pull the content field from a parsed object
      const extractContent = (obj) => {
        if (!obj || typeof obj !== 'object') return null
        return obj.content || obj.notes || obj.text || obj.body || null
      }

      // 4a. Try standard JSON.parse
      let jsonStr = trimmed
      // Strip markdown code fences if present
      const fenceMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/)
      if (fenceMatch) jsonStr = fenceMatch[1].trim()

      try {
        const parsed = JSON.parse(jsonStr)
        const extracted = extractContent(parsed)
        if (extracted) return String(extracted)
      } catch (e) {
        // 4b. JSON.parse failed — use regex to extract the "content" field value
        // This handles cases where the JSON has complex escaping that breaks JSON.parse
        const contentMatch = jsonStr.match(/"content"\s*:\s*"([\s\S]*?)"\s*,\s*"(?:key_|examples|mnemonics|estimated|difficulty|tags|summary)/)
        if (contentMatch) {
          try {
            // Unescape the JSON string value
            return JSON.parse('"' + contentMatch[1] + '"')
          } catch (e2) {
            // Return with basic unescaping
            return contentMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
          }
        }
      }

      // 5. Last resort — return raw text
      return trimmed
    }

    const aiEnhanceNote = async () => {
      if (!form.title) {
        alert.error('Error', 'Please enter a title first')
        return
      }
      aiEnhancing.value = true
      try {
        const response = await api.post('/v1/admin/admin-chat', {
          message: `Create comprehensive study notes for the topic: "${form.title}"\n\nPlease provide well-structured educational content with:\n\n# ${form.title}\n\n## Overview\nBrief introduction\n\n## Key Concepts\n- Important concepts with explanations\n\n## Detailed Explanation\nIn-depth coverage with examples\n\n## Important Points to Remember\n1. Key points\n\n## Summary\nBrief recap\n\nFormat in Markdown. Do not wrap in JSON.`,
          generation_type: 'notes',
          output_format: 'markdown'
        })
        const content = parseAINoteResponse(response)
        if (content && content.trim().length > 0) {
          form.content = content.trim()
          alert.success('Generated!', 'AI has created the note content')
        } else {
          alert.warning('Partial Success', 'AI responded but content was empty.')
        }
      } catch (error) {
        console.error('AI enhancement failed:', error)
        alert.error('Error', 'Failed to generate note with AI.')
      } finally {
        aiEnhancing.value = false
      }
    }

    // AI Translation
    const aiTranslateNote = () => {
      if (!form.title && !form.content) {
        alert.error('Error', 'Please enter a title or content first')
        return
      }
      showTranslateOptions.value = !showTranslateOptions.value
    }

    const confirmTranslateNote = async () => {
      if ((!form.title && !form.content) || !translateTargetLang.value) return
      const targetLangLabel = languageOptions.find(l => l.code === translateTargetLang.value)?.label || translateTargetLang.value
      aiTranslating.value = true
      try {
        const fieldsToTranslate = {}
        if (form.title) fieldsToTranslate.title = form.title
        if (form.content) fieldsToTranslate.content = form.content

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following educational note content to ${targetLangLabel}. Keep mathematical formulas, LaTeX expressions, Markdown formatting, and technical terms intact. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })
        const responseText = response.data?.response || response.data?.message || ''
        let aiData = response.data?.structured_data?.data || {}
        if (!aiData.title && !aiData.content) {
          const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
          if (jsonMatch) { try { aiData = JSON.parse(jsonMatch[1].trim()) } catch (e) {} }
          if (!aiData.title && !aiData.content) { try { aiData = JSON.parse(responseText) } catch (e) {} }
        }
        let fieldsUpdated = 0
        if (aiData.title) { form.title = aiData.title; fieldsUpdated++ }
        if (aiData.content) { form.content = aiData.content; fieldsUpdated++ }
        if (fieldsUpdated > 0) {
          form.language = translateTargetLang.value
          alert.success('Translated!', `${fieldsUpdated} field(s) translated to ${targetLangLabel}`)
        } else {
          alert.warning('Partial Success', 'AI responded but could not extract translated data.')
        }
        showTranslateOptions.value = false
      } catch (error) {
        console.error('AI translation failed:', error)
        alert.error('Error', 'Failed to translate note with AI.')
      } finally {
        aiTranslating.value = false
      }
    }

    // Fetch translation when language changes in edit mode
    const onLanguageChange = async () => {
      if (!isEditing.value || !editingId.value) return
      loadingTranslation.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'note', content_id: editingId.value, language_code: form.language }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }
          if (td.title) form.title = td.title
          if (td.content) form.content = td.content
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        loadingTranslation.value = false
      }
    }

    // Helpers
    const formatType = (type) => {
      if (!type) return '-'
      const types = { study_notes: 'Study Notes', topic_content: 'Topic Content', lesson_content: 'Lesson Content' }
      return types[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const getTypeBadge = (type) => {
      const badges = { study_notes: 'bg-info', topic_content: 'bg-primary', lesson_content: 'bg-warning' }
      return badges[type] || 'bg-secondary'
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    onMounted(fetchInitialData)

    return {
      breadcrumbs, columns, notes, loading, totalCount, currentPage, pageSize, hasMore,
      totalPages, startIndex, endIndex, visiblePages,
      exams, subjects, topics, lessons,
      loadingExams, loadingSubjects, loadingTopics, loadingLessons,
      selectedExam, selectedSubject, selectedTopic, filteredSubjects, filteredTopics,
      filterType, filterStatus, filterLanguage, emptyDescription,
      selectedExamName, selectedSubjectName, formLessons,
      canModify,
      showModal, showViewModal, isEditing, saving, viewingNote, aiEnhancing, form,
      aiTranslating, showTranslateOptions, translateTargetLang,
      loadingTranslation, onLanguageChange,
      onExamChange, onSubjectChange, goToPage, onPageSizeChange, fetchNotes,
      openCreateModal, openViewModal, openEditModal, saveNote, confirmDelete,
      aiEnhanceNote, aiTranslateNote, confirmTranslateNote, formatType, getTypeBadge, formatDate,
      languageOptions
    }
  }
}
</script>

<style scoped>
/* Filter Cascade Section */
.filter-cascade-section {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #a7f3d0;
}

.filter-cascade-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: #059669;
}

.filter-cascade-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.filter-cascade-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.filter-step {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 2px solid #e0e7ff;
  transition: all 0.3s ease;
  opacity: 0.6;
}

.filter-step.active {
  opacity: 1;
  border-color: #10b981;
}

.filter-step.completed {
  border-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
}

.filter-step.disabled {
  pointer-events: none;
  opacity: 0.4;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.step-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.optional-tag {
  font-weight: 400;
  color: #94a3b8;
  text-transform: none;
}

.filter-select {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-select:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.filter-select:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

/* Select Prompt */
.select-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #f9fafb;
  border-radius: 16px;
  border: 2px dashed #d1d5db;
}

.select-prompt-icon {
  color: #9ca3af;
  margin-bottom: 1.5rem;
}

.select-prompt h3 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  color: #374151;
}

.select-prompt p {
  margin: 0;
  color: #6b7280;
  max-width: 400px;
}

/* Notes Content */
.notes-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Modal Form */
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
  display: flex;
  align-items: center;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-control:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

/* Assignment Section */
.assignment-section {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #a7f3d0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8rem;
  color: #059669;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.assignment-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.assignment-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: white;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #059669;
  border: 1px solid #a7f3d0;
}

/* AI Enhance Section */
.ai-enhance-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  border: 1px solid #fbbf24;
  flex-wrap: wrap;
}

.ai-enhance-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-enhance-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.ai-enhance-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-translate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.ai-translate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.ai-hint {
  font-size: 0.8rem;
  color: #92400e;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.bg-primary { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; }
.bg-info { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); color: white; }
.bg-warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; }
.bg-secondary { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); color: white; }

.status-badge {
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.status-inactive {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}

/* Dark Mode */
:global(body.dark-mode) .filter-cascade-section {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #059669;
}

:global(body.dark-mode) .filter-cascade-header {
  color: #6ee7b7;
}

:global(body.dark-mode) .filter-step {
  background: #1e293b;
  border-color: #334155;
}

:global(body.dark-mode) .filter-step.completed {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #059669;
}

:global(body.dark-mode) .step-label {
  color: #94a3b8;
}

:global(body.dark-mode) .filter-select {
  background: #0f172a;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .select-prompt {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .select-prompt h3 {
  color: #f3f4f6;
}

:global(body.dark-mode) .select-prompt p {
  color: #9ca3af;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .assignment-section {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #059669;
}

:global(body.dark-mode) .section-label {
  color: #6ee7b7;
}

:global(body.dark-mode) .assignment-tag {
  background: #1e293b;
  border-color: #059669;
  color: #6ee7b7;
}

:global(body.dark-mode) .ai-enhance-section {
  background: linear-gradient(135deg, #78350f 0%, #92400e 100%);
  border-color: #b45309;
}

:global(body.dark-mode) .ai-translate-section {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .ai-hint {
  color: #fde68a;
}

/* Server-Side Pagination */
.server-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
  border-top: 1px solid #e5e7eb;
  margin-top: -1px;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0 0.5rem;
}

.pagination-page {
  min-width: 36px;
  height: 36px;
  padding: 0 0.5rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-page:hover:not(:disabled):not(.active) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-page.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #10b981;
  color: white;
}

.pagination-page.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}

.pagination-size {
  display: flex;
  align-items: center;
}

.page-size-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
}

.page-size-select:focus {
  outline: none;
  border-color: #10b981;
}

:global(body.dark-mode) .server-pagination {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .pagination-info {
  color: #9ca3af;
}

:global(body.dark-mode) .pagination-btn {
  background: #1e293b;
  border-color: #374151;
  color: #d1d5db;
}

:global(body.dark-mode) .pagination-btn:hover:not(:disabled) {
  background: #334155;
}

:global(body.dark-mode) .pagination-page {
  background: #1e293b;
  border-color: #374151;
  color: #d1d5db;
}

:global(body.dark-mode) .pagination-page:hover:not(:disabled):not(.active) {
  background: #334155;
}

:global(body.dark-mode) .page-size-select {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}
</style>
